/* A tab that lives in the URL rather than in memory.
 *
 * WHY. Tabs were useState, so a reload dropped you back on the first one —
 * you were reading Polls, refreshed, and landed on Feed. Worse, the browser
 * Back button skipped the whole screen instead of the tab you came from, and
 * a tab could not be linked to.
 *
 * The URL is the honest place for "which part of this screen am I looking
 * at?": it survives a reload, it works with Back and Forward, and
 * ?tab=polls can be pasted to somebody.
 *
 * REPLACE, NOT PUSH. Switching tabs replaces the history entry rather than
 * stacking one per tab — otherwise Back walks you through every tab you
 * touched before finally leaving the screen, which is not what Back means.
 *
 * An unknown or missing ?tab falls back to the default, so an old link, a
 * typo, or a tab that has since been removed can never render an empty
 * screen.
 */
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export function useTabParam<T extends string>(
  allowed: readonly T[],
  fallback: T,
  key = 'tab',
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams()

  const raw = params.get(key)
  const tab = (allowed as readonly string[]).includes(raw ?? '')
    ? (raw as T)
    : fallback

  const setTab = useCallback(
    (next: T) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev)
          // The default tab needs no parameter — a bare URL is the tidier
          // thing to copy, and it still resolves here.
          if (next === fallback) updated.delete(key)
          else updated.set(key, next)
          return updated
        },
        { replace: true },
      )
    },
    [setParams, fallback, key],
  )

  return [tab, setTab]
}
